import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import axios from "axios";
import { useParams } from "react-router-dom";
import AppLayout from "../../../components/Layout";
import LabelText from "../../../components/LabelText";
import {
  getDailyRecoveryReport,
  getSummaryPreIntervention,
} from "../../../services/adminController";
import { formatRupee } from "../../../utils";

dayjs.extend(customParseFormat);

/* ----------------------------- Types ----------------------------- */

interface DRRData {
  bucket: string;
  cohortName: string | null;
  totalBorrowers: string;
  totalOSAmount: string;
  totalTargetAmount: string | null;
  forTheDay: string;
  monthTillDate: string;
  totalLogin: string;
  totalConnect1600: string;
  totalConnectNon1600: string;
  totalConnect1600Per: string;
  totalConnectNon1600Per: string;
  ptp: string;
  churn1600: string;
  churnNon1600: string;
  averageCaseResource: string;
  targetAmount: string;
  totalConnectedCallMade: string;
  agentCallMade: string;
  dialerCallMade: string;
  ivrCallMade: string;
  emailSent: string;
  smsSent: string;
  agent1600CallMade: string;
  totalLegalNotice: string | null;
  todayTargetAmount: string;
  createdAt: string | null;
  updatedAt: string | null;
  ptp_1600: string | null;
}

interface SummaryData {
  lenderName: string;
  lastUpdated: string;
  totalBorrowerCount: number | string;
  totalLoanAmount: number | string;
  totalOSAmount: number | string;
  preInteOSAmount: number | string;
  preInteBorrowerCount: number | string;
  preInteLoanAmount: number | string;
  mtdTotal: number | string;
  targetAmount: number | string;
  targetAchievePer: number | string;
  nextAllocationDate: string;
  targetAmount_180_365: number | string;
  targetAmount_365_plus: number | string;
  loginCount_180_365: number | string;
  loginCount_365_plus: number | string;
  totalIvrCallMade: number | string;
  total_agent_1600_call_365_plus: number | string;
  totalAgentCall_365_plus: number | string;
  totalAgent1600_Call_180_365: number | string;
  totalAgentCall_180_365: number | string;
  ptp: number | string;
  ptp_1600: number | string | null;
}

/** Exactly what POST /v1/analysis/summary-update accepts. Nothing else is ever sent. */
interface UpdateSummaryPayload {
  loginCount_180_365: string;
  loginCount_365_plus: string;
  ptp: string;
  nextAllocationDate: string;
  target: string;
  targetAmount_180_365: string;
  targetAmount_365_plus: string;
  totalAgent1600_Call_180_365: string;
  totalAgentCall_180_365: string;
  totalAgentCall_365_plus: string;
  totalIvrCallMade: string;
  total_agent_1600_call_365_plus: string;
  ptp_1600: string;
}

type DisplayField = { label: string; value: string };

/* ----------------------------- Helpers ----------------------------- */

const NA = "NA";
const DATE_FORMAT = "DD/MM/YYYY";
// TODO: replace with the real client id (route param / auth context)
const CLIENT_ID = "nbfc3";
const STAGE = "STAGE_3";

const BUCKET_180_365 = "180-365 Days";
const BUCKET_365_PLUS = "365+ Days";

const toNumber = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const rupee = (v: unknown): string => {
  const n = toNumber(v);
  return n === null ? NA : String(formatRupee(n));
};

const plain = (v: unknown): string =>
  v === null || v === undefined || v === "" ? NA : String(v);

const percent = (v: unknown): string => {
  const n = toNumber(v);
  return n === null ? NA : `${n} %`;
};

const decimal = (v: unknown, digits = 2): string => {
  const n = toNumber(v);
  return n === null ? NA : n.toFixed(digits);
};

const formatDateTime = (v?: string | null) =>
  v ? dayjs(v).format("DD/MM/YYYY hh:mm:ss A") : NA;

/* ------------------------------ API ------------------------------ */

// NOTE: plain axios has no auth headers. If your other services use a
// configured axios instance (token / baseURL), swap it in here.
const SUMMARY_UPDATE_URL = `${process.env.REACT_APP_API_BASE_URL}/analysis/summary-update`;

const updateSummary = async (clientId: string, payload: UpdateSummaryPayload) => {
  const response = await axios.post(SUMMARY_UPDATE_URL, payload, {
    params: { clientId },
  });
  return response.data;
};

/* ------------------------- Summary fields ------------------------- */

const buildSummaryFields = (s: Partial<SummaryData>) => [
  // Basic information
  { label: "Lender Name", value: plain(s.lenderName) },
  { label: "Last Updated Date (Pre-Intervention)", value: formatDateTime(s.lastUpdated) },
  { label: "Next Allocation Date", value: plain(s.nextAllocationDate) },

  // Overall collection / borrower data
  { label: "Total Borrowers (All Time)", value: plain(s.totalBorrowerCount) },
  { label: "Total Original Principal (All Time)", value: rupee(s.totalLoanAmount) },
  { label: "Total Outstanding (All Time)", value: rupee(s.totalOSAmount) },
  { label: "Total Pre-Intervention Borrowers", value: plain(s.preInteBorrowerCount) },
  { label: "Pre-Intervention Loan Amount", value: rupee(s.preInteLoanAmount) },
  { label: "Total Outstanding (Pre-Intervention)", value: rupee(s.preInteOSAmount) },

  // Collection / target
  { label: "Month-to-Date (MTD) Collection", value: rupee(s.mtdTotal) },
  { label: "Monthly Target Amount", value: rupee(s.targetAmount) },
  { label: "Monthly Target Achieved", value: percent(s.targetAchievePer) },

  // Bucket-wise target
  { label: "Target Amount (180-365 Days)", value: rupee(s.targetAmount_180_365) },
  { label: "Target Amount (365+ Days)", value: rupee(s.targetAmount_365_plus) },

  // Login counts
  { label: "" },
  { label: "Login Count (180-365 Days)", value: plain(s.loginCount_180_365) },
  { label: "Login Count (365+ Days)", value: plain(s.loginCount_365_plus) },

  // Call data
  { label: "Total IVR Calls Made", value: plain(s.totalIvrCallMade) },

  { label: "Agent Calls  1600 (180-365 Days)", value: plain(s.totalAgent1600_Call_180_365) },

  { label: "Agent Calls Non 1600 (180-365 Days)", value: plain(s.totalAgentCall_180_365) },

  { label: "PTP 1600", value: plain(s.ptp_1600) },

  { label: "Agent Calls 1600 (365+ Days)", value: plain(s.total_agent_1600_call_365_plus) },

  { label: "Agent Calls Non 1600 (365+ Days)", value: plain(s.totalAgentCall_365_plus) },

  { label: "PTP Non 1600", value: plain(s.ptp) },
];

/* --------------------------- DRR fields --------------------------- */

const buildDrrFields = (r?: DRRData): DisplayField[] => [
  { label: "Total Borrowers", value: plain(r?.totalBorrowers) },
  { label: "Total OS Amount", value: rupee(r?.totalOSAmount) },
  { label: "Target Amount", value: rupee(r?.targetAmount) },
  { label: "Today Target Amount", value: rupee(r?.todayTargetAmount) },
  { label: "For The Day", value: rupee(r?.forTheDay) },
  { label: "Month Till Date", value: rupee(r?.monthTillDate) },
  { label: "Total Login", value: plain(r?.totalLogin) },
  { label: "Connect 1600", value: plain(r?.totalConnect1600) },
  { label: "Connect Non 1600", value: plain(r?.totalConnectNon1600) },
  { label: "Connect 1600 %", value: plain(r?.totalConnect1600Per) },
  { label: "Connect Non 1600 %", value: plain(r?.totalConnectNon1600Per) },
  { label: "PTP 1600", value: plain(r?.ptp_1600) },
  { label: "PTP Non 1600", value: plain(r?.ptp) },
  { label: "Churn 1600", value: decimal(r?.churn1600) },
  { label: "Churn Non 1600", value: decimal(r?.churnNon1600) },
  { label: "Avg Case Resource", value: plain(r?.averageCaseResource) },
  { label: "Total Connected Calls", value: plain(r?.totalConnectedCallMade) },
  { label: "Total Calls Non 1600", value: plain(r?.agentCallMade) },
  { label: "Total Calls 1600", value: plain(r?.agent1600CallMade) },
  { label: "Dialer Calls", value: plain(r?.dialerCallMade) },
  { label: "IVR Calls", value: plain(r?.ivrCallMade) },
  { label: "Email Sent", value: plain(r?.emailSent) },
  { label: "SMS Sent", value: plain(r?.smsSent) },
];

/* ------------------------ Reusable fields card ------------------------ */

interface FieldsCardProps {
  title: string;
  fields: DisplayField[];
  loading?: boolean;
  children?: React.ReactNode;
}

const FieldsCard = ({ title, fields, loading, children }: FieldsCardProps) => (
  <Card title={title} className="my-5" loading={loading}>
    {children}
    <div className="grid grid-cols-3 gap-4">
      {fields.map((f) => (
        <LabelText key={f.label} label={f.label} text={f.value} />
      ))}
    </div>
  </Card>
);

/* ------------------------ Update summary modal ------------------------ */

type FormValues = Omit<UpdateSummaryPayload, "nextAllocationDate"> & {
  nextAllocationDate: Dayjs | null;
};

type FieldKind = "count" | "amount" | "text";

interface FieldConfig {
  name: Exclude<keyof UpdateSummaryPayload, "nextAllocationDate">;
  label: string;
  kind: FieldKind;
}

const UPDATE_FIELDS: FieldConfig[] = [
  { name: "target", label: "Monthly Target Amount", kind: "amount" },
  { name: "targetAmount_180_365", label: "Target Amount (180-365 Days)", kind: "amount" },
  { name: "targetAmount_365_plus", label: "Target Amount (365+ Days)", kind: "amount" },
  { name: "loginCount_180_365", label: "Login Count (180-365 Days)", kind: "count" },
  { name: "loginCount_365_plus", label: "Login Count (365+ Days)", kind: "count" },
  { name: "totalIvrCallMade", label: "Total IVR Calls Made", kind: "count" },
  { name: "total_agent_1600_call_365_plus", label: "Agent 1600 Calls (365+ Days)", kind: "count" },
  { name: "totalAgentCall_365_plus", label: "Agent Calls Non 1600 (365+ Days)", kind: "count" },
  { name: "totalAgent1600_Call_180_365", label: "Agent 1600 Calls (180-365 Days)", kind: "count" },
  { name: "totalAgentCall_180_365", label: "Agent Calls Non 1600 (180-365 Days)", kind: "count" },
  { name: "ptp_1600", label: "PTP 1600", kind: "text" },
  { name: "ptp", label: "PTP Non 1600", kind: "text" },
];

const FIELD_RULES: Record<FieldKind, { pattern?: RegExp; message?: string }> = {
  count: { pattern: /^\d+$/, message: "Enter a whole number" },
  amount: { pattern: /^\d+(\.\d{1,2})?$/, message: "Enter a valid amount" },
  text: {},
};

const toStr = (v: unknown) => (v === null || v === undefined ? "" : String(v));

// The GET API may return DD/MM/YYYY or an ISO string, so try both.
const parseDate = (v?: string | null): Dayjs | null => {
  if (!v) return null;
  const strict = dayjs(v, DATE_FORMAT, true);
  if (strict.isValid()) return strict;
  const loose = dayjs(v);
  return loose.isValid() ? loose : null;
};

// GET calls the monthly target `targetAmount`; POST calls it `target`.
const toFormValues = (d: Partial<SummaryData>): FormValues => ({
  loginCount_180_365: toStr(d.loginCount_180_365),
  loginCount_365_plus: toStr(d.loginCount_365_plus),
  ptp: toStr(d.ptp),
  ptp_1600: toStr(d.ptp_1600),
  nextAllocationDate: parseDate(d.nextAllocationDate),
  target: toStr(d.targetAmount),
  targetAmount_180_365: toStr(d.targetAmount_180_365),
  targetAmount_365_plus: toStr(d.targetAmount_365_plus),
  totalAgent1600_Call_180_365: toStr(d.totalAgent1600_Call_180_365),
  totalAgentCall_180_365: toStr(d.totalAgentCall_180_365),
  totalAgentCall_365_plus: toStr(d.totalAgentCall_365_plus),
  totalIvrCallMade: toStr(d.totalIvrCallMade),
  total_agent_1600_call_365_plus: toStr(d.total_agent_1600_call_365_plus),
});

interface UpdateSummaryModalProps {
  open: boolean;
  loading?: boolean;
  initialData: Partial<SummaryData>;
  onCancel: () => void;
  onSubmit: (payload: UpdateSummaryPayload) => void;
}

const UpdateSummaryModal = ({
  open,
  loading = false,
  initialData,
  onCancel,
  onSubmit,
}: UpdateSummaryModalProps) => {
  const [form] = Form.useForm<FormValues>();

  // Autopopulate from the summary API every time the modal opens.
  useEffect(() => {
    if (open) {
      form.setFieldsValue(toFormValues(initialData));
    } else {
      form.resetFields();
    }
  }, [open, initialData, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    const { nextAllocationDate, ...rest } = values;

    const trimmed = Object.fromEntries(
      Object.entries(rest).map(([k, v]) => [k, String(v ?? "").trim()])
    ) as Omit<UpdateSummaryPayload, "nextAllocationDate">;

    onSubmit({
      ...trimmed,
      nextAllocationDate: nextAllocationDate
        ? nextAllocationDate.format(DATE_FORMAT)
        : "",
    });
  };

  return (
    <Modal
      title="Update summary"
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Save changes"
      confirmLoading={loading}
      maskClosable={false}
      width={720}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <div className="grid grid-cols-2 gap-x-4">
          {UPDATE_FIELDS.map(({ name, label, kind }) => (
            <Form.Item
              key={name}
              name={name}
              label={label}
              rules={[
                { required: true, message: `${label} is required` },
                ...(FIELD_RULES[kind].pattern
                  ? [
                      {
                        pattern: FIELD_RULES[kind].pattern,
                        message: FIELD_RULES[kind].message,
                      },
                    ]
                  : []),
              ]}
            >
              <Input />
            </Form.Item>
          ))}

          <Form.Item
            name="nextAllocationDate"
            label="Next Allocation Date"
            rules={[{ required: true, message: "Next allocation date is required" }]}
          >
            <DatePicker format={DATE_FORMAT} className="w-full" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

/* ----------------------------- Component ----------------------------- */

const DRR = () => {
  const { lenderId } = useParams<{ lenderId: string }>();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter that drives the DRR request
  const [date, setDate] = useState<Dayjs>(dayjs());

  const payload = useMemo(
    () => ({
      clientId: CLIENT_ID,
      date: date.format(DATE_FORMAT),
      stage: STAGE,
      status: "ALL",
    }),
    [date]
  );

  const {
    data: drrResponse,
    isLoading: isDrrLoading,
    isError: isDrrError,
  } = useQuery({
    queryKey: ["dailyRecoveryReport", payload],
    queryFn: () => getDailyRecoveryReport(payload),
  });

  const {
    data: summaryResponse,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useQuery({
    queryKey: ["summaryPreIntervention", lenderId],
    queryFn: () => getSummaryPreIntervention(lenderId as string),
    enabled: !!lenderId,
    // staleTime: 5 * 60 * 1000, // optional: 5 min cache
  });

  const summary: Partial<SummaryData> = isSummaryError
    ? {}
    : (summaryResponse?.data as Partial<SummaryData>) ?? {};

  const summaryFields = useMemo(() => buildSummaryFields(summary), [summary]);

  const drrRows: DRRData[] = useMemo(
    () => drrResponse?.data?.DRR ?? [],
    [drrResponse]
  );

  const drrFields180 = useMemo(
    () => buildDrrFields(drrRows.find((r) => r.bucket === BUCKET_180_365)),
    [drrRows]
  );
  const drrFields365 = useMemo(
    () => buildDrrFields(drrRows.find((r) => r.bucket === BUCKET_365_PLUS)),
    [drrRows]
  );

  const updateSummaryMutation = useMutation({
    mutationFn: (values: UpdateSummaryPayload) =>
      updateSummary(lenderId as string, values),
    onSuccess: () => {
      message.success("Summary updated");
      setIsModalOpen(false);
      // Refetch so the cards (and the next modal open) show the saved values.
      queryClient.invalidateQueries({
        queryKey: ["summaryPreIntervention", lenderId],
      });
    },
    onError: () => {
      message.error("Could not update the summary. Try again.");
    },
  });

  return (
    <AppLayout>
      {/* ---------- Summary ---------- */}
      <Card title="Summary" className="my-5" loading={isSummaryLoading}>
        {isSummaryError && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message="Could not load the pre-intervention summary."
          />
        )}

        <div className="grid grid-cols-3 gap-4">
          {summaryFields.map((field) => (
            <LabelText key={field.label} label={field.label} text={field.value} />
          ))}

          <div className="flex justify-between items-end">
            <Button
              type="primary"
              onClick={() => setIsModalOpen(true)}
              disabled={!lenderId || isSummaryLoading || isSummaryError}
            >
              Update Summary
            </Button>
          </div>
        </div>
      </Card>

      {/* ---------- DRR report ---------- */}
      <div className="flex justify-between items-center my-5">
        <h3 className="m-0 text-base font-semibold">DRR report</h3>
        <DatePicker
          value={date}
          format={DATE_FORMAT}
          allowClear={false}
          onChange={(d) => d && setDate(d)}
        />
      </div>

      {isDrrError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Could not load the daily recovery report."
        />
      )}

      <FieldsCard
        title={`DRR · ${BUCKET_180_365}`}
        fields={drrFields180}
        loading={isDrrLoading}
      />
      <FieldsCard
        title={`DRR · ${BUCKET_365_PLUS}`}
        fields={drrFields365}
        loading={isDrrLoading}
      />

      <UpdateSummaryModal
        open={isModalOpen}
        loading={updateSummaryMutation.isPending}
        initialData={summary}
        onCancel={() => setIsModalOpen(false)}
        onSubmit={(values) => updateSummaryMutation.mutate(values)}
      />
    </AppLayout>
  );
};

export default DRR;